import type { MaybeRefOrGetter } from 'vue';
import type { MonospaceBlock } from '#layers/data-model/public';
import type { GeneratedContentPage } from '#layers/spaces/utils/content-pages';
import { computed, toValue } from 'vue';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { getContentPageOverrideId } from '#layers/spaces/utils/content-pages';

/**
 * Resolves what a generated content page actually renders: the user's customization when there is
 * one, otherwise the generated page itself.
 *
 * Deliberately queries directly rather than reading `useWorkspacePagesStore`. This runs on the
 * content render path for one page at a time, where `cache-and-fetch` is the right trade — it
 * paints from cache and revalidates in the background, instead of blocking every content page on
 * a round trip. Authority for "does a customization exist" lives in the write path, not here.
 */
export function useContentPageOverride(page: MaybeRefOrGetter<GeneratedContentPage>) {
	const store = useWorkspaceEntityStore();

	const overridePageId = computed(() => getContentPageOverrideId(toValue(page)));

	const { data: queriedOverridePage } = store.MonospacePage.query((q) => q.first({
		filter: {
			overridePage: { _eq: overridePageId.value },
		},
		fetchPolicy: 'cache-and-fetch',
	}));

	// Re-check the id: while the page prop changes, the query still holds the previous page's
	// result, and rendering that would show one collection's customization under another.
	const overridePage = computed(() => queriedOverridePage.value?.overridePage === overridePageId.value
		? queriedOverridePage.value
		: undefined);

	const overridePageBlockId = computed(() => overridePage.value?.id);

	const { data: queriedOverridePageBlock } = store.MonospaceBlock.query((q) => q.first(
		overridePageBlockId.value
			? {
					filter: {
						pageId: { _eq: overridePageBlockId.value },
					},
					fetchPolicy: 'cache-and-fetch',
				}
			: { enabled: false },
	));

	const overridePageBlock = computed(() => queriedOverridePageBlock.value?.pageId === overridePageBlockId.value
		? queriedOverridePageBlock.value
		: undefined);

	const contentPage = computed(() => overridePage.value || toValue(page));

	const contentBlock = computed<MonospaceBlock | undefined>(() => (overridePage.value
		? overridePageBlock.value
		: toValue(page).block) ?? undefined);

	return {
		overridePage,
		contentPage,
		contentBlock,
	};
}
